import type { ApiError } from '@medicail/shared';
import { getAuthToken, notifyUnauthorized } from './authToken';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

function toUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/') || path === '/api') return path;
  return `/api${path.startsWith('/') ? '' : '/'}${path}`;
}

/** Same auth as apiFetch but returns the raw Response (SSE, CSV download). Never throws on non-2xx. */
export async function apiFetchRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = await getAuthToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(toUrl(path), { ...init, headers });
  if (res.status === 401) notifyUnauthorized();
  return res;
}

export async function errorFromResponse(res: Response): Promise<ApiRequestError> {
  let code = 'http_error';
  let message = res.statusText || `Request failed (${res.status})`;
  try {
    const body = (await res.clone().json()) as Partial<ApiError>;
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    /* non JSON body */
  }
  return new ApiRequestError(res.status, code, message);
}

export async function apiFetch<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
    rest.body = JSON.stringify(json);
    rest.method ??= 'POST';
  }
  const res = await apiFetchRaw(path, { ...rest, headers });
  if (!res.ok) throw await errorFromResponse(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Friendly message for any thrown value. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof ApiRequestError) return err.message || fallback;
  if (err instanceof Error && err.name !== 'AbortError') return err.message || fallback;
  return fallback;
}
